import { supabase } from '../lib/supabase'

export type DepartmentRelation =
  | { display_name?: string | null; name?: string | null }
  | Array<{ display_name?: string | null; name?: string | null } | null>
  | null
  | undefined

export function departmentLabel(department?: DepartmentRelation | string | null): string | null {
  if (!department) return null
  if (typeof department === 'string') {
    const label = department.trim()
    return label || null
  }
  const dept = Array.isArray(department) ? department.find(Boolean) : department
  const label = (dept?.display_name || dept?.name || '').trim()
  return label || null
}

/** Nome visível: "Maria Silva (Comercial)". Sem departamento, devolve só o nome. */
export function formatUserNameWithDepartment(
  name?: string | null,
  department?: DepartmentRelation | string | null,
  fallback = 'Usuário',
): string {
  const base = (name || '').trim() || fallback
  const dept = departmentLabel(department)
  return dept ? `${base} (${dept})` : base
}

export function formatAuthorName(
  author?: { name?: string | null; department?: DepartmentRelation } | null,
  fallback = 'Usuário',
): string {
  return formatUserNameWithDepartment(author?.name, author?.department, fallback)
}

export type ProfileDisplayRow = {
  id: string
  name: string | null
  avatar_url: string | null
  departmentName: string | null
}

export async function fetchProfilesDisplay(ids: string[]): Promise<ProfileDisplayRow[]> {
  const unique = [...new Set(ids.filter(Boolean))]
  if (unique.length === 0) return []

  const withDepartment = await supabase
    .from('profiles')
    .select('id, name, avatar_url, department:departments(display_name, name)')
    .in('id', unique)

  if (!withDepartment.error) {
    return (withDepartment.data || []).map((row) => ({
      id: row.id as string,
      name: (row.name as string | null) ?? null,
      avatar_url: (row.avatar_url as string | null) ?? null,
      departmentName: departmentLabel((row as { department?: DepartmentRelation }).department),
    }))
  }

  const fallback = await supabase
    .from('profiles')
    .select('id, name, avatar_url')
    .in('id', unique)

  if (fallback.error) throw fallback.error

  return (fallback.data || []).map((row) => ({
    id: row.id,
    name: row.name,
    avatar_url: row.avatar_url,
    departmentName: null,
  }))
}

const departmentLabelCache = new Map<string, string | null>()
let queuedIds: string[] = []
let flushPromise: Promise<void> | null = null

export function peekDepartmentLabel(userId: string): string | null | undefined {
  return departmentLabelCache.has(userId) ? departmentLabelCache.get(userId) : undefined
}

async function fetchDepartmentLabels(ids: string[]): Promise<void> {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, department:departments(display_name, name)')
    .in('id', ids)

  if (error) {
    const { data: fallback } = await supabase
      .from('profiles')
      .select('id, department_id')
      .in('id', ids)

    const deptIds = [
      ...new Set((fallback || []).map((row) => row.department_id).filter(Boolean)),
    ] as string[]
    const deptById = new Map<string, { display_name?: string | null; name?: string | null }>()

    if (deptIds.length > 0) {
      const { data: departments } = await supabase
        .from('departments')
        .select('id, display_name, name')
        .in('id', deptIds)
      for (const dept of departments || []) deptById.set(dept.id, dept)
    }

    for (const row of fallback || []) {
      departmentLabelCache.set(
        row.id,
        departmentLabel(row.department_id ? deptById.get(row.department_id) : null),
      )
    }
  } else {
    for (const row of data || []) {
      departmentLabelCache.set(
        row.id as string,
        departmentLabel((row as { department?: DepartmentRelation }).department),
      )
    }
  }

  for (const id of ids) {
    if (!departmentLabelCache.has(id)) departmentLabelCache.set(id, null)
  }
}

/** Resolve o departamento de usuários citados (@) para exibir junto do nome. */
export function ensureDepartmentLabels(userIds: string[]): Promise<void> {
  for (const id of userIds) {
    if (id && !departmentLabelCache.has(id) && !queuedIds.includes(id)) queuedIds.push(id)
  }

  if (!flushPromise) {
    flushPromise = Promise.resolve().then(async () => {
      const ids = queuedIds.splice(0)
      flushPromise = null
      if (ids.length > 0) await fetchDepartmentLabels(ids)
    })
  }

  return flushPromise.then(() => {
    const pending = userIds.filter((id) => id && !departmentLabelCache.has(id))
    if (pending.length > 0) return ensureDepartmentLabels(pending)
  })
}
